import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Lock, Upload, Trash2, FileCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOC_LABELS = { rera: "RERA Certificate", khata: "Khata Extract", oc: "Occupancy Certificate", sale_deed: "Sale Deed", tax_receipt: "Tax Receipt", other: "Other" };

export const DocumentVault = ({ property, onAuth, openSignal = 0 }) => {
  const { user } = useAuth();
  const [state, setState] = useState({ unlocked: false, documents: [] });
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("rera");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  const load = useCallback(() => {
    if (!property?.id) return;
    api.get(`/properties/${property.id}/documents`).then(({ data }) => setState(data)).catch(() => {});
  }, [property]);

  useEffect(() => { load(); }, [load]);

  const isOwner = user && user.id === property?.owner_id;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/properties/${property.id}/documents?doc_type=${docType}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      load();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/properties/${property.id}/documents/${id}`);
    load();
  };

  if (!state.documents.length && !isOwner) return null;

  return (
    <div className="p-5 rounded-2xl border border-[var(--line)] bg-white" data-testid="document-vault">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-[var(--emerald)]" />
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)]">Document Vault</span>
        </div>
        {isOwner && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-ghost text-xs py-1.5 px-3" data-testid="upload-doc-btn">
                <Upload className="w-3.5 h-3.5" /> Add
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm" data-testid="upload-doc-dialog">
              <DialogHeader><DialogTitle className="font-serif text-xl">Upload document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Document type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOC_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="doc-file-input"
                />
                <button onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-primary w-full">
                  {uploading ? "Uploading…" : "Choose file (PDF/image, max 12MB)"}
                </button>
                <p className="text-[10px] text-[var(--ink-muted)]">Buyers unlock after their first confirmed visit.</p>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!state.unlocked && !isOwner && state.documents.length > 0 && (
        <div className="mb-3 p-3 rounded-xl bg-[rgba(212,163,89,0.1)] border border-[rgba(212,163,89,0.3)] flex items-center gap-2 text-xs text-[var(--teak)]" data-testid="vault-locked-notice">
          <Lock className="w-3.5 h-3.5 text-[var(--gold)]" />
          <span>
            {user ? "Book a visit to unlock the full document vault" : "Sign in and book a visit to unlock these documents"}
          </span>
        </div>
      )}

      {state.documents.length === 0 ? (
        <div className="text-xs text-[var(--ink-muted)] italic">No documents uploaded yet.</div>
      ) : (
        <div className="space-y-2">
          {state.documents.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-3 rounded-xl bg-[var(--surface)]" data-testid={`doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {d.unlocked ? <FileText className="w-4 h-4 text-[var(--emerald)] shrink-0" /> : <Lock className="w-4 h-4 text-[var(--ink-muted)] shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--teak)]">{DOC_LABELS[d.doc_type] || d.doc_type}</div>
                  <div className="text-[11px] text-[var(--ink-muted)] truncate">
                    {d.original_filename} · {(d.size / 1024).toFixed(0)} KB
                  </div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {d.unlocked ? (
                  <a href={`${BACKEND}${d.url}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs py-1 px-2" data-testid={`doc-open-${d.id}`}>View</a>
                ) : (
                  !user ? (
                    <button onClick={() => onAuth?.("login")} className="btn-ghost text-xs py-1 px-2">Sign In</button>
                  ) : null
                )}
                {isOwner && (
                  <button onClick={() => remove(d.id)} className="p-1 rounded hover:bg-white" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-[var(--ink-muted)]" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {state.unlocked && state.documents.length > 0 && (
        <div className="mt-3 text-[10px] text-[var(--emerald)] flex items-center gap-1">
          <FileCheck className="w-3 h-3" /> Full vault unlocked
        </div>
      )}
    </div>
  );
};
